import { useState } from 'react';
import { Gift, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';
import GiftingWizard from '@/components/gifting/GiftingWizard';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GiftingPage = () => {
  const [showWizard, setShowWizard] = useState(false);
  const [showCorporateForm, setShowCorporateForm] = useState(false);
  const [corporateForm, setCorporateForm] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    quantity: '',
    message: ''
  });
  const [submitting, setSubmitting] = useState(false);

  const handleCorporateSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, null, { 
        params: {
          name: corporateForm.name,
          email: corporateForm.email,
          subject: `Corporate Gifting Inquiry - ${corporateForm.company}`,
          message: `Company: ${corporateForm.company}\nPhone: ${corporateForm.phone}\nQuantity: ${corporateForm.quantity} pieces\n\nMessage: ${corporateForm.message}`,
          type: 'corporate'
        }
      });
      toast.success('Thank you! We\'ll get back to you within 24 hours.');
      setShowCorporateForm(false);
      setCorporateForm({ name: '', email: '', company: '', phone: '', quantity: '', message: '' });
    } catch (error) {
      toast.error('Failed to submit. Please try again or email us directly.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="pt-32 pb-24" data-testid="gifting-page">
      {/* Hero */}
      <section className="relative min-h-[60vh] flex items-center">
        <div className="absolute inset-0">
          <img 
            src="/images/products/IMG_6256.jpg"
            alt="Luxury gift packaging"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-background/90 to-background/40" />
        </div>
        
        <div className="relative chaleur-container">
          <div className="max-w-xl">
            <p className="caption text-primary mb-4">Gifting with Purpose</p>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-light mb-6">
              The Art of <span className="editorial-italic">Meaningful</span> Giving
            </h1>
            <p className="text-muted-foreground mb-8">
              Every Chaleur piece is more than a gift—it's a story, a connection, 
              a moment of warmth shared between souls. Let us help you find the perfect 
              expression of care.
            </p>
            <Button 
              className="btn-chaleur-primary"
              onClick={() => setShowWizard(true)}
              data-testid="start-gifting-wizard"
            >
              <Gift className="mr-2 w-4 h-4" /> Find the Perfect Gift
            </Button>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="section-spacing">
        <div className="chaleur-container">
          <div className="text-center mb-16">
            <p className="caption text-primary mb-4">How It Works</p>
            <h2 className="text-3xl md:text-4xl font-light">Your Guided Gifting Experience</h2>
          </div>

          <div className="grid md:grid-cols-4 gap-8">
            {[
              { step: '01', title: 'Who', desc: 'Tell us about the recipient—mother, partner, friend, or yourself' },
              { step: '02', title: 'Occasion', desc: 'Birthday, anniversary, festive celebration, or just because' },
              { step: '03', title: 'Budget', desc: 'Set your range and we\'ll find options that fit perfectly' },
              { step: '04', title: 'Style', desc: 'Minimal, vibrant, traditional, or luxe—match their taste' }
            ].map((item, idx) => (
              <div key={idx} className="text-center">
                <div className="text-4xl font-light text-primary mb-4">{item.step}</div>
                <h3 className="text-xl mb-2">{item.title}</h3>
                <p className="text-sm text-muted-foreground">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Gift Services */}
      <section className="section-spacing bg-secondary/30">
        <div className="chaleur-container">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="aspect-square overflow-hidden">
              <img 
                src="/images/products/IMG_6257.jpg"
                alt="Gift wrapping"
                className="w-full h-full object-cover"
              />
            </div>
            <div>
              <p className="caption text-primary mb-4">Premium Services</p>
              <h2 className="text-3xl md:text-4xl font-light mb-6">
                Make It <span className="editorial-italic">Unforgettable</span>
              </h2>
              <div className="space-y-6">
                <div className="flex gap-4">
                  <div className="w-12 h-12 bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Gift className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h4 className="font-medium mb-1">Luxury Gift Wrapping</h4>
                    <p className="text-sm text-muted-foreground">
                      Handcrafted packaging with artisan-made paper and silk ribbons
                    </p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="w-12 h-12 bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <svg className="w-6 h-6 text-primary" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <path d="M19 5H5a2 2 0 00-2 2v10a2 2 0 002 2h14a2 2 0 002-2V7a2 2 0 00-2-2z" />
                      <path d="M3 7l9 6 9-6" />
                    </svg>
                  </div>
                  <div>
                    <h4 className="font-medium mb-1">Personal Message Card</h4>
                    <p className="text-sm text-muted-foreground">
                      Hand-calligraphed notes on artisan paper included with your gift
                    </p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="w-12 h-12 bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <svg className="w-6 h-6 text-primary" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" />
                      <circle cx="9" cy="7" r="4" />
                      <path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75" />
                    </svg>
                  </div>
                  <div>
                    <h4 className="font-medium mb-1">Ship to Someone Else</h4>
                    <p className="text-sm text-muted-foreground">
                      Direct delivery to your recipient with gift receipt (no prices shown)
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Corporate Gifting */}
      <section className="section-spacing">
        <div className="chaleur-container">
          <div className="max-w-3xl mx-auto text-center">
            <p className="caption text-primary mb-4">Corporate Gifting</p>
            <h2 className="text-3xl md:text-4xl font-light mb-6">
              Thoughtful Gifts for Your Team & Clients
            </h2>
            <p className="text-muted-foreground mb-8">
              Elevate your corporate gifting with Chaleur. Custom packaging, bulk pricing, 
              and dedicated account management for orders of 20+ pieces.
            </p>
            <Button 
              variant="outline" 
              className="btn-chaleur-secondary"
              onClick={() => setShowCorporateForm(true)}
            >
              Inquire About Corporate Orders
            </Button>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-spacing bg-foreground text-primary-foreground">
        <div className="chaleur-container text-center">
          <p className="caption text-white/60 mb-4">Ready to Give?</p>
          <h2 className="text-3xl md:text-4xl font-light mb-6">
            Let Us Help You Find the Perfect Gift
          </h2>
          <p className="text-white/70 max-w-2xl mx-auto mb-8">
            Answer a few quick questions and we'll curate personalized recommendations 
            that carry warmth, meaning, and purpose.
          </p>
          <Button 
            className="btn-chaleur-primary bg-white text-foreground hover:bg-white/90"
            onClick={() => setShowWizard(true)}
          >
            <Gift className="mr-2 w-4 h-4" /> Start Gifting Guide
          </Button>
        </div>
      </section>

      <GiftingWizard open={showWizard} onOpenChange={setShowWizard} />

      {/* Corporate Inquiry Modal */}
      {showCorporateForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div 
            className="absolute inset-0 bg-black/50" 
            onClick={() => setShowCorporateForm(false)}
          />
          <div className="relative bg-background w-full max-w-lg mx-4 p-8 max-h-[90vh] overflow-y-auto">
            <button 
              onClick={() => setShowCorporateForm(false)}
              className="absolute top-4 right-4 p-2 hover:bg-secondary rounded-full transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
            
            <h3 className="text-2xl font-light mb-2">Corporate Gifting Inquiry</h3>
            <p className="text-muted-foreground mb-6">
              Fill out the form below and we'll get back to you within 24 hours.
            </p>
            
            <form onSubmit={handleCorporateSubmit} className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm mb-2 block">Your Name *</Label>
                  <Input
                    value={corporateForm.name}
                    onChange={(e) => setCorporateForm({ ...corporateForm, name: e.target.value })}
                    className="rounded-none"
                    required
                  />
                </div>
                <div>
                  <Label className="text-sm mb-2 block">Email *</Label>
                  <Input
                    type="email"
                    value={corporateForm.email}
                    onChange={(e) => setCorporateForm({ ...corporateForm, email: e.target.value })}
                    className="rounded-none"
                    required
                  />
                </div>
              </div>
              
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm mb-2 block">Company Name *</Label>
                  <Input
                    value={corporateForm.company}
                    onChange={(e) => setCorporateForm({ ...corporateForm, company: e.target.value })}
                    className="rounded-none"
                    required
                  />
                </div>
                <div>
                  <Label className="text-sm mb-2 block">Phone Number</Label>
                  <Input
                    value={corporateForm.phone}
                    onChange={(e) => setCorporateForm({ ...corporateForm, phone: e.target.value })}
                    className="rounded-none"
                  />
                </div>
              </div>
              
              <div>
                <Label className="text-sm mb-2 block">Estimated Quantity *</Label>
                <Input
                  value={corporateForm.quantity}
                  onChange={(e) => setCorporateForm({ ...corporateForm, quantity: e.target.value })}
                  placeholder="e.g., 50-100 pieces"
                  className="rounded-none"
                  required
                />
              </div>
              
              <div>
                <Label className="text-sm mb-2 block">Tell us about your requirements</Label>
                <Textarea
                  value={corporateForm.message}
                  onChange={(e) => setCorporateForm({ ...corporateForm, message: e.target.value })}
                  placeholder="Occasion, budget range, customization needs, timeline..."
                  className="rounded-none min-h-[100px]"
                />
              </div>
              
              <Button 
                type="submit" 
                className="w-full btn-chaleur-primary"
                disabled={submitting}
              >
                {submitting ? 'Submitting...' : 'Submit Inquiry'}
              </Button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default GiftingPage;
